import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import OxbridgeEditorialFooter from './OxbridgeEditorialFooter';
import './OxbridgeLandingPage.css';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel, trackClassName }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="oxbridge-scroll-carousel">
      <button
        type="button"
        className={`oxbridge-carousel-arrow oxbridge-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`oxbridge-carousel-arrow oxbridge-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="oxbridge-card-scroll" ref={scrollRef}>
        <div className={`oxbridge-card-track ${trackClassName || ''}`}>
          {children}
        </div>
      </div>
      <div className="oxbridge-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function OxbridgeLandingPage() {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const interviewFormats = [
    { course: "Oxford Law", interviews: "2–3, per college", format: "Subject tutorial", focus: "Unseen legal reasoning, statutory interpretation" },
    { course: "Oxford PPE", interviews: "2, separate strands", format: "Subject tutorials", focus: "Political theory, economic & philosophical argument" },
    { course: "Oxford Economics & Management", interviews: "2, per college", format: "Subject tutorial", focus: "Quantitative reasoning, applied economic scenarios" },
    { course: "Oxford Physics", interviews: "2–3, per college", format: "Technical problem-solving", focus: "Live physics & maths problems, whiteboard reasoning" },
    { course: "Cambridge Computer Science", interviews: "2, per college", format: "Technical & logic", focus: "Algorithmic thinking, mathematical proof" },
    { course: "Cambridge Natural Sciences", interviews: "2–4, by options chosen", format: "Subject-specific tutorials", focus: "Biological, chemical, physical & interdisciplinary reasoning" },
    { course: "Cambridge Engineering", interviews: "2–3, per college", format: "Technical problem-solving", focus: "Mechanics, structures, materials, estimation & systems problems" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across three foundations spanning verbal-protocol research, Oxford tutorial pedagogy and executive-function science, so it reasons through a live interview the way an Oxbridge tutor or supervisor actually would",
        "Finds exactly where a spoken argument stops holding up under follow-up, so you correct the actual reasoning gap, not just the final answer",
        <>The world's first <button type="button" className="oxbridge-inline-link oxbridge-link-btn" onClick={() => navigate('/method/oxbridge')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="oxbridge-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Behind Foundation II",
      title: "The Oxford Tutorial Tradition",
      meta: "Sustained One-to-One Challenge",
      items: [
        "OxCHEPS research names sustained one-to-one challenge the tutorial's defining feature",
        "The same pressure IVORI applies — whether your answer was right or wrong",
        "Documented across decades of real Oxford and Cambridge teaching practice"
      ]
    },
    {
      tag: "Behind Foundation III",
      title: "Diamond's Executive-Function",
      meta: "Cognitive Flexibility Under Pressure",
      items: [
        "Diamond's research on executive function: updating a view when new facts arrive",
        "The exact skill an interviewer tests by interrupting with new information",
        "Published in the Annual Review of Psychology, among the field's most-cited work"
      ]
    },
    {
      tag: "Behind Foundation I",
      title: "Verbal-Protocol Research",
      meta: "Reasoning Captured Live",
      items: [
        "Ericsson and Simon's protocol analysis treats reasoning spoken aloud as real data, not noise",
        "The same principle behind every live-voice IVORI tutorial and interview drill",
        "A spoken argument reveals a process a written answer alone can't show"
      ]
    },
    {
      tag: "Three Skills, One Discipline",
      title: "Three Skills, Three Traditions",
      meta: "Trained on All Three Deliberately",
      items: [
        "Thinking Aloud is trained on Ericsson and Simon's verbal-protocol research — scoring the reasoning you build live, not only the answer you land on",
        "Handling Pressure is trained on the real Oxford tutorial tradition — sustained one-to-one challenge on your weakest point, exactly as a tutor or supervisor runs it",
        "Adapting in Real Time is trained on Diamond's executive-function research — updating an argument the instant new information changes the question"
      ]
    },
    {
      tag: "Foundation I",
      title: "Thinking Aloud",
      meta: "Live-Voice Tutorials & Supervisions",
      items: [
        "Oxford tutorials and Cambridge supervisions assess how you think, not just your final answer",
        "AI probes your reasoning live, sentence by sentence, the way an actual tutor would",
        "Structured feedback on clarity and structure, not just correctness"
      ]
    },
    {
      tag: "Foundation II",
      title: "Handling Pressure",
      meta: "Follow-Up Drilling Under Live Pressure",
      items: [
        "Every answer is challenged, whether it was right or wrong, exactly as a real tutorial runs",
        "Practice defending a position without folding at the first pushback",
        "Learn precisely when to hold your ground and when the evidence means you should revise"
      ]
    },
    {
      tag: "Foundation III",
      title: "Adapting in Real Time",
      meta: "New Information Mid-Answer",
      items: [
        "Interviewers introduce new facts mid-answer specifically to see whether your argument updates",
        "Drills reward genuine adaptation over a rehearsed, pre-loaded answer",
        "Feedback shows exactly where reasoning stayed static when the question changed under you"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting shortlisted Year 12 and 13 candidates with Oxbridge interview preparation once admissions test results place them in front of a college's interview panel.",
      slug: "handsworth-grammar",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Clifton's Team · Careers Department",
      body: "Piloted IVORI's Oxbridge interview simulator with a Year 12 cohort transitioning into Year 13, rehearsing subject tutorials and supervisions for candidates shortlisted across Oxford and Cambridge PPE and Economics.",
      slug: "clifton-college",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Interview Readiness"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending Oxbridge interview preparation across its family of Birmingham grammar schools.",
      slug: "king-edward-vi-foundation",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for Oxbridge interview practice, supporting shortlisted Year 12 and 13 students across every one of Oxbridge's seven most-interviewed courses.",
      slug: "grammar-schools",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Interview Prep"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for Oxbridge interview practice, supporting shortlisted Year 12 and 13 students with subject-specific tutorial and supervision rehearsal.",
      slug: "independent-schools",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Interview Prep"
    }
  ];

  const faqs = [
    {
      q: "What is the Oxbridge interview, and why does it matter?",
      a: [
        "Oxford and Cambridge interview shortlisted applicants as part of admissions, typically in one or more subject-specific tutorials or supervisions. Rather than testing memorized knowledge, interviewers assess how candidates reason live — building an argument, responding to challenge, and adapting when given new information.",
        "This live-reasoning discipline is exactly what IVORI's Oxbridge interview tutoring and prep is built to train, rather than a bank of questions with a single correct answer."
      ]
    },
    {
      q: "What is IVORI's pedagogical method for Oxbridge interview prep, and who's behind it?",
      a: [
        "Every simulation, follow-up and feedback pattern is reviewed by admissions specialists and former Oxbridge interviewers, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just whether an answer held up, but exactly where it stopped holding up.",
        "IVORI trains on three research traditions, one per interview skill: Ericsson and Simon's verbal-protocol research for thinking aloud, the real Oxford tutorial tradition for handling sustained pressure, and Diamond's executive-function research for adapting when the question changes mid-answer. Underneath all three sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's IVORI Lab's proprietary foundation decision world model — a system trained to reason about decision-making under uncertainty, rather than simply predict text. IVORI's Oxbridge products sit on top of this underlying model, using its proprietary training methodology to run live follow-up pressure and score a candidate's reasoning the way a real tutor or supervisor would."
      ]
    },
    {
      q: "Which courses does IVORI's Oxbridge interview coaching cover?",
      a: ["IVORI covers subject-specific tutorial and supervision simulations across seven of Oxbridge's most-interviewed courses: Oxford Law, Oxford PPE, Oxford Economics & Management, Oxford Physics, Cambridge Computer Science, Cambridge Natural Sciences and Cambridge Engineering, alongside general Personal Statement and motivation interview practice."]
    },
    {
      q: "How do IVORI's Oxbridge mock interviews work?",
      a: [
        "Each mock opens with a real admissions-style question, then applies live follow-up pressure the way an actual tutor or supervisor would — pushing on your weakest point, or introducing new information mid-answer.",
        "You receive structured feedback on reasoning clarity, responsiveness to pressure, and how well you adapted your position, so you know exactly which of the three underlying skills still needs work before the real interview."
      ]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'oxbridge.ivoriedu.com';
  const onIvoriAiEdu = host === 'oxbridge.edu.ivoriai.com';
  const oxbridgeUrl = onIvoriEdu
    ? 'https://oxbridge.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://oxbridge.edu.ivoriai.com/'
      : (host.includes('oxbridge') ? 'https://oxbridge.edu.ivoriai.com/' : 'https://ivoriai.com/edu/oxbridge');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="oxbridge-landing-root">
      <SeoMeta
        title="IVORI — Oxbridge Interview Prep"
        description="AI-powered Oxbridge interview prep and coaching — live tutorial simulations, follow-up-pressure drilling and panel mocks from IVORI Edu."
        keywords="oxbridge interview prep, oxford interview prep, cambridge interview prep, oxford law interview, oxford ppe interview, cambridge computer science interview, cambridge natural sciences interview, cambridge engineering interview, oxbridge tutorial practice, ivori oxbridge, oxford interview acceptance rate, cambridge supervision practice, oxbridge admissions interview questions, thinking aloud protocol oxbridge"
        url={oxbridgeUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "Oxbridge Interview Preparation",
                "description": "AI-powered Oxbridge interview prep from IVORI Edu, trained on three foundations spanning verbal-protocol research, Oxford tutorial pedagogy and executive-function science — live tutorial and supervision simulations, follow-up-pressure drilling, and panel interview mocks across Oxford Law, PPE, Economics & Management, Physics, and Cambridge Computer Science, Natural Sciences and Engineering.",
                "provider": {
                  "@type": "Organization",
                  "name": "ivori",
                  "url": eduHomeUrl
                },
                "url": oxbridgeUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Live tutorial and supervision reasoning under follow-up pressure",
                  "Thinking aloud as a trainable verbal-protocol skill",
                  "Adapting an argument when new information changes the question",
                  "Subject-specific tutorial practice across seven Oxbridge courses"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "Oxbridge Interview Preparation", "item": oxbridgeUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="oxbridge-hero-section">
        <div className="oxbridge-container oxbridge-hero-content">
          <span className="oxbridge-mono-eyebrow">IVORI Edu</span>
          <h1 className="oxbridge-hero-title">Master the Oxbridge Interview.</h1>
          <p className="oxbridge-hero-stat">Oxbridge interview prep across all seven of Oxford &amp; Cambridge's most-interviewed courses.</p>
          <p className="oxbridge-hero-tagline">Train the live reasoning the Oxbridge interview actually rewards.</p>
          <button type="button" className="oxbridge-hero-cta" onClick={() => navigate('/pricing?package=oxbridge')}>See plans</button>
        </div>
      </section>

      {/* INTERVIEW FORMAT MATRIX */}
      <section id="oxbridge-matrix" className="oxbridge-editorial-section">
        <div className="oxbridge-container">
          <div className="oxbridge-editorial-section-head">
            <span className="oxbridge-mono-eyebrow">Course-By-Course Breakdown</span>
            <h2>Oxford &amp; Cambridge Interview Formats</h2>
            <p>Oxford Law interview prep, Oxford PPE interview prep, Oxford Economics &amp; Management interview prep, Oxford Physics interview prep, Cambridge Computer Science interview prep, Cambridge Natural Sciences interview prep and Cambridge Engineering interview prep — what to expect at interview across Oxbridge's seven most-interviewed courses.</p>
          </div>

          <ScrollCarousel ariaLabel="course">
            {interviewFormats.map((row, idx) => (
              <div className="oxbridge-matrix-card" key={idx}>
                <div className="oxbridge-matrix-uni">{row.course}</div>
                <div className="oxbridge-matrix-row"><span>Typical Interviews</span><b>{row.interviews}</b></div>
                <div className="oxbridge-matrix-row"><span>Format</span><b>{row.format}</b></div>
                <div className="oxbridge-matrix-row oxbridge-score-cell"><span>Primary Focus</span><b>{row.focus}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* THE IVORI METHOD */}
      <section className="oxbridge-editorial-section">
        <div className="oxbridge-container">
          <div className="oxbridge-editorial-section-head">
            <span className="oxbridge-mono-eyebrow">How It Works</span>
            <h2>The IVORI Method</h2>
            <p>Three skills no paper exam ever tests — and the three an Oxbridge interview is actually built around</p>
          </div>

          <ScrollCarousel ariaLabel="skill">
            {structureCards.map((card, idx) => (
              <div className="oxbridge-structure-card" key={idx}>
                <span className="oxbridge-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="oxbridge-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE INTERVIEW */}
      <section className="oxbridge-editorial-section">
        <div className="oxbridge-container">
          <div className="oxbridge-editorial-section-head">
            <span className="oxbridge-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI runs a live tutorial across all seven Oxbridge courses — including the follow-up pressure a real interviewer would apply</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Oxford Law Tutorial · Opening Question</span>
              <div className="oxbridge-passage">
                "Consider a doctor who administers a lethal dose of pain relief to a terminally ill patient, primarily intending to relieve suffering, but knowing death will result sooner as a consequence."
              </div>
              <h3>Should the doctor be held criminally liable for the patient's death?</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">Does intention change the analysis if the outcome is identical either way?</div>
              <div className="oxbridge-option">What if the patient had asked to die, but couldn't legally consent?</div>
              <div className="oxbridge-option">Would your answer differ if the doctor were a family member, not a physician?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>Strong answers distinguish act from omission, address causation directly, and engage with intention (mens rea) rather than treating outcome alone as decisive.</p>
              <p>IVORI tracks whether each follow-up genuinely shifts your reasoning, or whether you repeat the same point in different words — the second is what tutors notice fastest.</p>
              <p>Feedback is structured around the same dimensions a real tutor scores: legal precision, responsiveness to pressure, and whether your position stays coherent as the scenario changes.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Oxford PPE Tutorial · Opening Question</span>
              <div className="oxbridge-passage">
                "A government announces it will double the national minimum wage overnight."
              </div>
              <h3>What would you expect to happen to unemployment, and why?</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Live Update — New Information</span>
              <div className="oxbridge-passage">
                "Suppose new data shows labour demand in this sector is highly inelastic — firms can't easily reduce headcount even as costs rise. Does that change your answer?"
              </div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>A rehearsed answer defends the original prediction regardless of the new premise. A strong candidate updates the conclusion — lower expected job losses — while explaining exactly why the mechanism changed.</p>
              <p>IVORI flags "static" answers that ignore the interjection, and rewards responses that show the reasoning actually moving in response to new information.</p>
              <p>This is the same adaptability tutors probe for across PPE, Economics &amp; Management and Natural Sciences interviews, not just this one scenario.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Oxford Economics &amp; Management · Opening Question</span>
              <div className="oxbridge-passage">
                "A firm operating in a perfectly competitive market currently earns zero economic profit."
              </div>
              <h3>Should the firm exit the market?</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">What if the firm's fixed costs are sunk and can't be recovered by exiting?</div>
              <div className="oxbridge-option">Does your answer change if the firm expects prices to rise next year?</div>
              <div className="oxbridge-option">How would you distinguish economic profit from accounting profit here?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>Zero economic profit already means the firm is covering its opportunity cost — a candidate who treats "zero profit" as a crisis signal misses this immediately.</p>
              <p>IVORI checks whether sunk costs are correctly excluded from the exit decision, and whether the candidate incorporates the forward-looking price expectation rather than reasoning only from the static snapshot.</p>
              <p>This is exactly the applied, quantitative reasoning Economics &amp; Management tutors are listening for — not textbook recall of the theory.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Oxford Physics · Opening Question</span>
              <div className="oxbridge-passage">
                "A ball is dropped from rest and falls under gravity alone, with air resistance ignored."
              </div>
              <h3>Sketch how you'd derive its velocity as a function of time — then explain what changes if you add air resistance proportional to velocity.</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">What changes physically once resistance is proportional to velocity squared instead?</div>
              <div className="oxbridge-option">Can you identify the terminal velocity condition without solving the full differential equation?</div>
              <div className="oxbridge-option">How would your model change on the Moon?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates reach terminal velocity by reasoning that net force must be zero, rather than grinding through the full differential equation — a shortcut that signals real physical intuition.</p>
              <p>IVORI tracks whether the candidate can move from a clean idealized case to a modified physical scenario live, articulating the physics driving each equation rather than just manipulating algebra.</p>
              <p>This shift from idealized to real-world modelling is the core of an Oxford Physics tutorial.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Cambridge Computer Science · Opening Question</span>
              <div className="oxbridge-passage">
                "You are given an unsorted array of n integers and asked to find two elements that sum to a given target value."
              </div>
              <h3>What is the most efficient algorithm you can devise, and what is its time complexity?</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">Can you improve on an O(n²) brute-force approach?</div>
              <div className="oxbridge-option">What if the array is already sorted — does that change your approach or complexity?</div>
              <div className="oxbridge-option">How would you handle duplicate values in the array?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates move from an O(n²) brute-force scan to a hash-map approach that solves the problem in O(n) time, and can articulate the space-for-time trade-off explicitly.</p>
              <p>IVORI checks whether the candidate genuinely adapts the algorithm when the constraint changes — a sorted array opens up a two-pointer approach — rather than defending the first idea regardless of new information.</p>
              <p>Reasoning about edge cases like duplicates live, not after being prompted, is what separates a strong Cambridge Computer Science interview from an average one.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Cambridge Natural Sciences · Opening Question</span>
              <div className="oxbridge-passage">
                "A population of moths has two colour morphs. The darker morph has become far more common in industrial areas with polluted, darker tree bark."
              </div>
              <h3>What evolutionary explanation would you propose for this observation?</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">How would you test whether this is natural selection rather than genetic drift?</div>
              <div className="oxbridge-option">What experimental evidence would falsify your hypothesis?</div>
              <div className="oxbridge-option">How might this same mechanism generalize across biological and chemical systems?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>A strong answer proposes camouflage-driven predation as the selective pressure, then immediately asks what data would distinguish this from drift — rather than treating the first plausible story as proof.</p>
              <p>IVORI listens for genuinely falsifiable experimental design, and for reasoning that stays interdisciplinary — connecting biological selection to the underlying chemistry of pigmentation — rather than staying in one lane.</p>
              <p>This is the cross-disciplinary reasoning Cambridge Natural Sciences interviews are built to surface, whichever science options a candidate has chosen.</p>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Cambridge Engineering · Opening Question</span>
              <div className="oxbridge-passage">
                "You are asked to estimate the maximum load a simply supported wooden beam can carry before failure, given its dimensions and material."
              </div>
              <h3>Walk through your approach to estimating this, including which physical principles you'd apply.</h3>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">Follow-Up Pressure</span>
              <div className="oxbridge-option">How would your estimate change if the beam were cantilevered instead of simply supported?</div>
              <div className="oxbridge-option">What assumptions are you making about material properties, and how would you validate them?</div>
              <div className="oxbridge-option">How does moisture or temperature affect wood's structural properties in your model?</div>
            </div>

            <div className="oxbridge-qcard-panel">
              <span className="oxbridge-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates identify bending stress and moment of inertia as the governing quantities, and reason explicitly about the beam's failure mode rather than jumping straight to a formula.</p>
              <p>IVORI checks whether assumptions — material uniformity, load type, support conditions — are stated up front and then genuinely revised when the scenario changes, not defended out of habit.</p>
              <p>Estimation under stated, revisable assumptions is exactly what Cambridge Engineering interviews are testing for.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="oxbridge-editorial-section">
        <div className="oxbridge-container">
          <div className="oxbridge-editorial-section-head">
            <span className="oxbridge-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>IVORI Edu's Oxbridge interview coaching runs from Handsworth Grammar Oxbridge interview prep and a Clifton College Oxbridge interview pilot, to King Edward VI Foundation Oxbridge interview readiness, Grammar Schools Oxbridge interview practice and Independent Schools Oxbridge interview practice nationwide.</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="oxbridge-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="oxbridge-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="oxbridge-school-more">
                  Find out more about{' '}
                  <button type="button" className="oxbridge-inline-link oxbridge-link-btn" onClick={() => navigate(`/case-studies/${school.slug}`)}>
                    {school.name}
                  </button>
                </p>
                <div className="oxbridge-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* FAQ */}
      <section className="oxbridge-editorial-section oxbridge-faq-editorial">
        <div className="oxbridge-container">
          <div className="oxbridge-editorial-section-head">
            <span className="oxbridge-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, mock formats and how IVORI's live feedback works</p>
          </div>

          <div className="oxbridge-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`oxbridge-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="oxbridge-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="oxbridge-faq-plus">+</span>
                </div>
                <div className="oxbridge-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OxbridgeEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}
